/* hs-eslint ignored failing-rules */

/* eslint-disable hubspot-dev/no-unsafe-storage */

/* eslint-disable no-bitwise */
'use es6';

import PortalIdParser from 'PortalIdParser';
import Raven from 'raven-js';
import LocalStorageClient from 'DesignData/lib/localStorage/LocalStorageClient';
import { LOCAL_STORAGE_LEGACY_KEY_ENCOUNTERED } from 'DesignData/enums/sentry/SentryMessages';
import * as PortalKeys from 'DesignData/enums/attributes/Portal';
import * as UserKeys from 'DesignData/enums/attributes/User';
import * as UserPortalKeys from 'DesignData/enums/attributes/UserPortal';
import { toSlug } from 'DesignData/lib/text';
import { CACHE_EXPIRATION_PERIOD } from 'DesignData/constants/LocalStorage';
/**
 * @param {Object} session - `true` if session data has expired, `false` otherwise.
 */

export function isExpired(session) {
  return !!(session && session.updated && Date.now() - session.updated > CACHE_EXPIRATION_PERIOD);
}
/**
 * Creates a timestamp for items missing an `updated` timestamp.
 *
 * @param {Float} elapsePercentage - Elapse period. E.g. `0.6` leaves 40% of period before expiry.
 * @returns {Number}
 */

export function createUpdatedTimestampForMissing(elapsePercentage = 0.7) {
  return Date.now() - (CACHE_EXPIRATION_PERIOD * elapsePercentage | 0);
}
/**
 *
 * @param {String} suffix
 */

export function createLegacyStorageKey(suffix) {
  return `design-manager:${PortalIdParser.get()}:${suffix}`;
}
/**
 *
 * @param {LocalStorageClient} client
 * @param {String}             legacySuffix
 * @param {Object}             options
 * @returns {Promise|Null|*}
 */

export function tryConvertLegacyStorage(client, legacySuffix, options) {
  options = options || {
    async: true
  };
  const legacyKey = createLegacyStorageKey(legacySuffix);
  const legacy = localStorage.getItem(legacyKey);

  if (legacy === null) {
    return null;
  }

  let data;

  try {
    Raven.captureMessage(LOCAL_STORAGE_LEGACY_KEY_ENCOUNTERED);
    localStorage.removeItem(legacyKey);
    data = JSON.parse(legacy); // Attempt writing to IDE key

    client.save(data);
  } catch (error) {//
  }

  const result = data !== undefined ? data : legacy;
  return options.async ? Promise.resolve(result) : result;
}
/**
 * @type {String}
 */

export const KEY_PREFIX = 'design-manager-ide';
/**
 * @type {String}
 */

export const LEGACY_KEY_PREFIX = 'design-manager';
/**
 * TODO: Add named capture groups. At this time (2019/05) supports them.
 * @type {RegExp}
 */

export const KEY_REGEX = /^(design-manager-ide)(?:\:user-(\d+))?(?:\:portal-(\d+))?(?:\:(.+))$/; // Named -> /^(?<prefix>design-manager-ide)(?:\:user-(?<userId>\d+))?(?:\:portal-(?<portalId>\d+))?(?:\:(?<key>.+))$/;

/**
 * Creates a Design Manager specific localStorage key.
 *
 * @param {String} key        - Required
 * @param {Number} userId     - Optional
 * @param {Number} portalId   - Optional
 * @returns {String}
 */

function createKey(key, userId, portalId) {
  const parts = [KEY_PREFIX];

  if (userId != null) {
    parts.push(`user-${userId}`);
  }

  if (portalId != null) {
    parts.push(`portal-${portalId}`);
  }

  const slug = toSlug(key);
  parts.push(slug);
  return parts.join(':');
}

function validateClientIdParam(name, value) {
  if (value !== +value) {
    throw new TypeError(`Invalid localStorage client param "${name}": ${value}`);
  }
}

function validateClientKeyParam(client, value, keys) {
  if (!keys[value]) {
    throw new TypeError(`Invalid API key for ${client} localStorage client: ${value}`);
  }
}

const cache = {};
/**
 * @param {Object} keyFields
 * @returns {LocalStorageClient}
 */

function getOrCreateClient({
  key,
  userId,
  portalId
}) {
  const __key = createKey(key, userId, portalId);

  if (cache[__key]) {
    return cache[__key];
  }

  const client = new LocalStorageClient(__key);
  cache[__key] = client;
  return client;
}
/**
 * Gets a user specific localStorage client.
 * @param {Number} userId
 * @param {String} key
 * @returns {LocalStorageClient}
 * @throws {TypeError}
 */


export function user(userId, key) {
  validateClientIdParam('userId', userId);
  validateClientKeyParam('User', key, UserKeys);
  return getOrCreateClient({
    userId,
    key
  });
}
/**
 * Get a portal specific localStorage client.
 * @param {Number} portalId
 * @param {String} key
 * @returns {LocalStorageClient}
 * @throws {TypeError}
 */

export function portal(portalId, key) {
  validateClientIdParam('portalId', portalId);
  validateClientKeyParam('Portal', key, PortalKeys);
  return getOrCreateClient({
    portalId,
    key
  });
}
/**
 * Get a user+portal specific localStorage client.
 * @param {Number} userId
 * @param {Number} portalId
 * @param {String} key
 * @returns {LocalStorageClient}
 * @throws {TypeError}
 */

export function userPortal(userId, portalId, key) {
  validateClientIdParam('userId', userId);
  validateClientIdParam('portalId', portalId);
  validateClientKeyParam('UserPortal', key, UserPortalKeys);
  return getOrCreateClient({
    userId,
    portalId,
    key
  });
}